/** @jsxImportSource @emotion/react */
import PropTypes from "prop-types";
import Drawer from "@mui/material/Drawer";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { Box, Skeleton } from "@mui/material";
import t from "../../../theme";

const CategorySidebar = ({
  classes,
  categoryList,
  isCategoryLoading,
  selCategory,
  showLeftDrawer,
  drawerWidth,
  onCategorySelect,
  onClose,
}) => (
  <Drawer
    anchor={"left"}
    variant="persistent"
    open={showLeftDrawer}
    css={[
      classes.sideDrawer,
      {
        width: drawerWidth,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: "border-box",
          marginTop: 60,
          height: `calc(100% - 60px)`,
          paddingTop: 10,
          paddingBottom: 10,
          overflowY: "overlay",
          overflowX: "hidden",
          background: t.glass.secondary,
          backdropFilter: t.blur.heavy,
          WebkitBackdropFilter: t.blur.heavy,
          borderRight: `1px solid ${t.border.colorBase}`,
          boxShadow: "1px 0 3px " + t.border.colorSubtle,
          transition: t.transition.smooth,
          scrollbarWidth: "thin",
          scrollbarColor: "transparent transparent",
          "&:hover": {
            scrollbarColor: `${t.green.scrollbar} transparent`,
          },
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "transparent",
            borderRadius: "3px",
            transition: "background 0.3s ease",
          },
          "&:hover::-webkit-scrollbar-thumb": {
            background: t.green.scrollbar,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: t.green.scrollbarHover,
          },
        },
      },
    ]}
    sx={{
      ".MuiListItemButton-root": {
        borderBottom: `solid 1px ${t.border.colorSubtle}`,
        padding: "6px 12px",
        margin: "4px 6px",
        borderRadius: t.radius.xl,
        transition: t.transition.smooth,
        minHeight: "32px",
        border: "solid 1px transparent",
        "&:hover": {
          transition: t.transition.linear,
          background: t.green.bg,
          border: `solid 1px ${t.green.border}`,
        },
      },
      ".Mui-selected": {
        background: `${t.green.bgSelected} !important`,
        border: `1px solid ${t.green.borderSelected}`,
        fontWeight: "600",
      },
      ".MuiTypography-root": {
        fontSize: "0.75rem",
        lineHeight: "1.1",
        letterSpacing: "0.15px",
        fontWeight: "500",
        color: t.text.primary,
      },
      ".Mui-selected .MuiTypography-root": {
        color: t.text.accentDark,
        fontWeight: "600",
      },
    }}
    onClose={onClose}
  >
    {(() => {
      // Show skeleton until we have the city-sorted response (backend adds mails_count only
      // when city_uuid is supplied). Without this check the raw alphabetical list flashes
      // before the sorted-by-unread version arrives.
      const hasSortedData =
        categoryList?.length > 0 && "mails_count" in (categoryList[0] ?? {});
      const isReady = hasSortedData && !!selCategory;
      return isCategoryLoading || (categoryList?.length > 0 && !isReady);
    })()
      ? Array.from({ length: 6 }).map((_, i) => (
          <Box key={i} sx={{ px: "7px", py: "3px" }}>
            <Skeleton
              variant="rounded"
              height={36}
              sx={{
                borderRadius: "25px",
                bgcolor: "rgba(0,0,0,0.07)",
              }}
            />
          </Box>
        ))
      : (categoryList ?? []).map((list, i) => (
          <ListItem key={i} component="div" disablePadding>
            <ListItemButton
              selected={selCategory === list.uuid}
              onClick={() => onCategorySelect(list.uuid)}
              sx={{ ".Mui-selected": { background: "#efefef" } }}
            >
              {list.image && (
                <img
                  src={list.image}
                  alt={list.name}
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                    marginRight: "8px",
                  }}
                />
              )}
              <ListItemText primary={`${list.name}`} />
              {list.mails_count > 0 && (
                <span css={classes.menuBadge}>{list.mails_count}</span>
              )}
            </ListItemButton>
          </ListItem>
        ))}
  </Drawer>
);

CategorySidebar.propTypes = {
  classes: PropTypes.object.isRequired,
  categoryList: PropTypes.array,
  isCategoryLoading: PropTypes.bool,
  selCategory: PropTypes.string,
  showLeftDrawer: PropTypes.bool.isRequired,
  drawerWidth: PropTypes.number.isRequired,
  onCategorySelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CategorySidebar;
